import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AIAssistant from './AIAssistant';

const Layout = () => {
  const { darkMode, toggleDarkMode } = useProjects();
  const { logout, user } = useAuth();
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Derive display name from user email (strip @domain)
  const displayName = user?.email ? user.email.split('@')[0].toUpperCase() : 'Admin';

  const navItems = [
    { path: '/', icon: FiIcons.FiGrid, label: 'Dashboard' },
    { path: '/projects', icon: FiIcons.FiLayers, label: 'Projects' },
    { path: '/signed-off', icon: FiIcons.FiCheckCircle, label: 'Signed Off' },
    { path: '/archive', icon: FiIcons.FiArchive, label: 'Archive' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className={`min-h-screen flex transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Sidebar */}
      <aside className={`w-20 lg:w-72 flex-shrink-0 flex flex-col border-r ${darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className="h-16 flex items-center justify-center lg:justify-start lg:px-6 border-b border-opacity-10 border-gray-500">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
            C
          </div>
          <span className="hidden lg:block ml-3 font-bold text-lg tracking-tight">CreativeFlow <span className="text-indigo-500 text-sm font-medium">by MW</span></span>
        </div>

        <nav className="flex-1 py-6 space-y-2 px-3">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `
                flex items-center px-4 py-3 rounded-xl transition-all duration-200 group
                ${isActive 
                  ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-medium' 
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400'}
              `}
            >
              <SafeIcon icon={item.icon} className="text-xl" />
              <span className="hidden lg:block ml-3">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-opacity-10 border-gray-500 space-y-2">
          <button
            onClick={() => setIsAiOpen(true)}
            className="w-full flex items-center justify-center lg:justify-start px-3 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-indigo-500/30 transition-all"
          >
            <SafeIcon icon={FiIcons.FiZap} className="text-xl" />
            <span className="hidden lg:block ml-3 font-medium">AI Assistant</span>
          </button>
          
          <button
            onClick={handleLogout}
            className="w-full flex lg:hidden items-center justify-center px-3 py-3 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all"
          >
            <SafeIcon icon={FiIcons.FiLogOut} className="text-xl" />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-0 h-screen">
        {/* Top Header */}
        <header className={`h-16 flex items-center justify-between px-6 border-b ${darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
          <h1 className="text-xl font-semibold capitalize">
            {location.pathname === '/' ? 'Dashboard' : location.pathname.split('/')[1].replace('-', ' ')}
          </h1>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleDarkMode}
              className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-800 text-yellow-400' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <SafeIcon icon={darkMode ? FiIcons.FiSun : FiIcons.FiMoon} className="text-xl" />
            </button>
            
            <div className="relative">
              <button 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center border-2 border-white dark:border-gray-700 overflow-hidden hover:ring-2 ring-indigo-500 transition-all"
              >
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&q=80" alt="User" />
              </button>

              {isUserMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)}></div>
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-2 z-20 animate-fade-in-up">
                    <div className="px-4 py-2 border-b dark:border-gray-700">
                      <p className="text-sm font-bold dark:text-white">{displayName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Administrator</p>
                    </div>
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                    >
                      <SafeIcon icon={FiIcons.FiLogOut} />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>

      {/* AI Assistant Modal/Overlay */}
      <AIAssistant isOpen={isAiOpen} onClose={() => setIsAiOpen(false)} />
    </div>
  );
};

export default Layout;